import { ItemsCount } from 'components/ui/ItemsCount/ItemsCount';
import {
  CompanySettingChangeOrderField,
  CompanySettingChangeSource,
  SortOption,
  useCompanySettingChangesQuery,
} from 'generated/graphql';
import { useCallback, useMemo, useState } from 'react';
import {
  HeaderSortDirection,
  Search,
  SelectFormField,
  Table,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  DEFAULT_AUDIT_PAGE_SIZE,
  parseTableData,
  settingKeysMatching,
  sourceFilterOptions,
  tableHeaders,
  unsortableHeaderKeys,
} from './AuditLogTab.helpers';
import styles from './AuditLogTab.module.scss';

interface IAuditLogTab {
  companyId: number;
}

/**
 * Read-only history of settings changes made on this company through the
 * admin app: who changed what, when, from what to what. Server-side
 * pagination, sorting, and filtering (source + setting search).
 */
export function AuditLogTab({ companyId }: IAuditLogTab) {
  const { handleMsgType } = useNotification();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_AUDIT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<TSelectOption>(sourceFilterOptions[0]);
  const [orderBy, setOrderBy] = useState<CompanySettingChangeOrderField>(
    CompanySettingChangeOrderField.OccurredAt,
  );
  const [orderDirection, setOrderDirection] = useState<SortOption>(
    SortOption.Desc,
  );

  const { data, loading } = useCompanySettingChangesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      companyId,
      pagination: { page, size: pageSize },
      order: { field: orderBy, direction: orderDirection },
      ...(search
        ? { settingSearch: search, settingKeys: settingKeysMatching(search) }
        : {}),
      ...(source.value
        ? { source: source.value as CompanySettingChangeSource }
        : {}),
    },
    onError: (error) => {
      handleMsgType({
        type: TNotification.error,
        title: 'Unable to load the audit log',
        message: error.message || 'Something went wrong. Please try again.',
        dismissable: true,
      });
    },
  });

  const tableRows = useMemo(
    () => (data?.companySettingChanges.data ?? []).map(parseTableData),
    [data?.companySettingChanges.data],
  );
  const itemsTotal = data?.companySettingChanges.pageInfo?.itemsTotal ?? 0;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleSourceChange = (option: TSelectOption) => {
    setSource(option ?? sourceFilterOptions[0]);
    setPage(1);
  };

  const handleSortChange = useCallback(
    (field: string, tableSortDirection: HeaderSortDirection) => {
      if (tableSortDirection === HeaderSortDirection.NONE) {
        // Clearing the sort falls back to the default: newest first.
        setOrderBy(CompanySettingChangeOrderField.OccurredAt);
        setOrderDirection(SortOption.Desc);
        return;
      }
      setOrderBy(field as CompanySettingChangeOrderField);
      setOrderDirection(
        tableSortDirection === HeaderSortDirection.ASC
          ? SortOption.Asc
          : SortOption.Desc,
      );
    },
    [],
  );

  const externalSort = useMemo(
    () => ({
      sortDirection:
        orderDirection === SortOption.Asc
          ? HeaderSortDirection.ASC
          : HeaderSortDirection.DESC,
      onClick: handleSortChange,
      sortedHeader: orderBy,
    }),
    [orderBy, orderDirection, handleSortChange],
  );

  return (
    <div className={styles.container}>
      <Table
        headerData={tableHeaders}
        rowData={tableRows}
        loading={loading}
        unsortableHeaderKeys={unsortableHeaderKeys}
        externalSort={externalSort}
        tableSearchDirty={Boolean(search) || Boolean(source.value)}
        hasPagination
        pagination={{
          currentPageSize: pageSize,
          onChange: ({
            page: newPage,
            pageSize: newPageSize,
          }: {
            page: number;
            pageSize: number;
          }) => {
            setPage(newPageSize === pageSize ? newPage : 1);
            setPageSize(newPageSize);
          },
          totalNumberOfItems: itemsTotal,
          page,
        }}
        hasToolbar
        toolbarConf={{
          componentListLeftStart: (
            <ItemsCount
              count={itemsTotal}
              ariaLabelSuffix="changes in the audit log"
            />
          ),
          componentListLeft: (
            <div className={styles.toolbarContainer}>
              <form
                onSubmit={(event) => event.preventDefault()}
                className={styles.searchForm}
              >
                <Search
                  internalSearch={false}
                  onSearch={handleSearch}
                  inputValue={search}
                  onClearBtnClick={() => handleSearch('')}
                  ariaLabel="Search settings"
                  ariaDisabled={false}
                />
              </form>
              <div className={styles.sourceSelect}>
                <SelectFormField
                  id="audit-log-source-filter"
                  label=""
                  ariaLabel="Filter by source"
                  placeholder="All sources"
                  options={sourceFilterOptions}
                  value={source}
                  onChange={handleSourceChange}
                />
              </div>
            </div>
          ),
        }}
      />
    </div>
  );
}
